import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useDataProviderContext } from '@/shared/contexts/DataProvider'

import type { AsyncConfig, Primitive, SelectOption } from './types'
import { safeGet, toStr } from './utils'

type OptionLookup = Map<string, SelectOption>

interface UseSelectOptionsResult {
  options: SelectOption[]
  loading: boolean
  error: string | null
  reload: () => void
  optionLookup: OptionLookup
}

export function useSelectOptions(
  staticOptions: SelectOption[] | undefined,
  asyncCfg: AsyncConfig | undefined,
  searchTerm: string
): UseSelectOptionsResult {
  const { dataProvider } = useDataProviderContext()
  const [options, setOptions] = useState<SelectOption[]>(staticOptions ?? [])
  const [optionLookup, setOptionLookup] = useState<OptionLookup>(() => {
    const map = new Map<string, SelectOption>()
    ;(staticOptions ?? []).forEach(opt => map.set(toStr(opt.value), opt))
    return map
  })
  const [loading, setLoading] = useState<boolean>(!!asyncCfg)
  const [error, setError] = useState<string | null>(null)

  const staticSignature = useMemo(
    () => JSON.stringify(staticOptions ?? []),
    [staticOptions]
  )

  useEffect(() => {
    const normalized = staticOptions ?? []

    if (asyncCfg?.url) {
      setOptions(prev => {
        const map = new Map<string, SelectOption>()
        normalized.forEach(opt => map.set(toStr(opt.value), opt))
        prev.forEach(opt => map.set(toStr(opt.value), opt))
        return Array.from(map.values())
      })

      setOptionLookup(prev => {
        const next = new Map(prev)
        normalized.forEach(opt => next.set(toStr(opt.value), opt))
        return next
      })
      return
    }

    setOptions(normalized)
    setOptionLookup(() => {
      const map = new Map<string, SelectOption>()
      normalized.forEach(opt => map.set(toStr(opt.value), opt))
      return map
    })
    setLoading(false)
  }, [asyncCfg?.url, staticSignature])

  const norm = useMemo(() => {
    if (!asyncCfg) return null
    const url = asyncCfg.url
    const perPage = asyncCfg.perPage ?? 100
    const sortField = asyncCfg.sort?.field ?? 'name'
    const sortOrder = asyncCfg.sort?.order ?? 'asc'
    const filterStr = JSON.stringify(asyncCfg.filter ?? {})
    const labelKeyTag =
      typeof asyncCfg.labelKey === 'string' ? asyncCfg.labelKey : 'fn'
    const valueKeyTag =
      typeof asyncCfg.valueKey === 'string' ? asyncCfg.valueKey : 'fn'
    const searchParam = asyncCfg.searchParam ?? 'name_cont'
    return {
      url,
      perPage,
      sortField,
      sortOrder,
      filterStr,
      labelKeyTag,
      valueKeyTag,
      searchParam
    }
  }, [
    asyncCfg?.url,
    asyncCfg?.perPage,
    asyncCfg?.sort?.field,
    asyncCfg?.sort?.order,
    JSON.stringify(asyncCfg?.filter ?? {}),
    typeof asyncCfg?.labelKey === 'string' ? asyncCfg?.labelKey : 'fn',
    typeof asyncCfg?.valueKey === 'string' ? asyncCfg?.valueKey : 'fn',
    asyncCfg?.searchParam
  ])

  const lastSigRef = useRef<string>('')
  const normalizedSearch =
    typeof searchTerm === 'string'
      ? searchTerm.trim()
      : searchTerm !== undefined && searchTerm !== null
        ? String(searchTerm).trim()
        : ''
  const sig = norm
    ? [
        norm.url,
        norm.perPage,
        norm.sortField,
        norm.sortOrder,
        norm.filterStr,
        norm.labelKeyTag,
        norm.valueKeyTag,
        normalizedSearch,
        norm.searchParam
      ].join('|')
    : ''

  const load = useCallback(
    async (force = false) => {
      if (!asyncCfg?.url || !norm) return
      if (!force && lastSigRef.current === sig) return
      lastSigRef.current = sig

      setLoading(true)
      setError(null)
      try {
        const response = await dataProvider.getList(asyncCfg.url, {
          pagination: { page: 1, perPage: norm.perPage },
          sort: {
            field: norm.sortField,
            order: norm.sortOrder as 'asc' | 'desc'
          },
          filter: {
            ...(asyncCfg.filter ?? {}),
            ...(normalizedSearch
              ? { [norm.searchParam]: normalizedSearch }
              : {})
          }
        })

        const records: any[] = Array.isArray(response?.records)
          ? response.records
          : []

        const getLabel =
          typeof asyncCfg.labelKey === 'function'
            ? asyncCfg.labelKey
            : (record: any) =>
                String(safeGet(record, asyncCfg.labelKey ?? 'name') ?? '')

        const getValue =
          typeof asyncCfg.valueKey === 'function'
            ? asyncCfg.valueKey
            : (record: any) =>
                safeGet(record, asyncCfg.valueKey ?? 'id') as Primitive

        const mapped = records.map(record => ({
          value: getValue(record),
          label: getLabel(record),
          record
        }))

        const mergedMap = new Map<string, SelectOption>()
        ;(staticOptions ?? []).forEach(opt =>
          mergedMap.set(toStr(opt.value), opt)
        )
        mapped.forEach(opt => mergedMap.set(toStr(opt.value), opt))
        const merged = Array.from(mergedMap.values())

        setOptions(merged)
        setOptionLookup(prev => {
          const next = new Map(prev)
          merged.forEach(opt => next.set(toStr(opt.value), opt))
          return next
        })
      } catch (nextError: any) {
        console.error('Select load error:', nextError)
        setError(nextError?.message ?? 'Erro ao carregar opções')
      } finally {
        setLoading(false)
      }
    },
    [asyncCfg, norm, sig, dataProvider, staticOptions, normalizedSearch]
  )

  useEffect(() => {
    if (!norm) return
    void load()
  }, [load, norm])

  useEffect(() => {
    if (asyncCfg?.url) return
    setOptions(staticOptions ?? [])
    setLoading(false)
  }, [asyncCfg?.url, staticSignature, staticOptions])

  const reload = useCallback(() => {
    lastSigRef.current = ''
    void load(true)
  }, [load])

  return { options, loading, error, reload, optionLookup }
}
